import React, { useEffect, useState } from 'react';
import { Table, Card, Typography, Input, Space, Button, Modal, Form, InputNumber, Select, message } from 'antd';
import { SearchOutlined, EditOutlined, SaveOutlined, CloseOutlined, PlusOutlined } from '@ant-design/icons';
import { fetchCountryDetailsData, fetchPointConversionData, savePointConversionData, updatePointConversionData } from '../../Services/dataServices';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../../authConfig';
import { PointConversionResponse, CountryDetails } from '../../Types/entities';

const { Title } = Typography;

const PointConversion: React.FC = () => {
    const { instance, accounts } = useMsal();
    const [conversionData, setConversionData] = useState<PointConversionResponse[]>([]);
    const [filteredData, setFilteredData] = useState<PointConversionResponse[]>([]);
    const [countryData, setCountryData] = useState<CountryDetails[]>([]);
    const [searchText, setSearchText] = useState('');
    const [loading, setLoading] = useState(false);
    const [editModalVisible, setEditModalVisible] = useState(false);
    const [addModalVisible, setAddModalVisible] = useState(false);
    const [editingRecord, setEditingRecord] = useState<PointConversionResponse | null>(null);
    const [form] = Form.useForm();
    const userId = localStorage.getItem("UserId");

    useEffect(() => {
        fetchToken();
        getPointConversionData();
        getCountryDetailsData();
    }, []);

    const fetchToken = async () => {
        const res = await instance.acquireTokenSilent({
            ...loginRequest,
            account: accounts[0]
        });
        return res.idToken;
    };

    const getPointConversionData = async () => {
        setLoading(true);
        try {
            const result = await fetchPointConversionData(instance, accounts);
            const data = result.apiResponse?.data || [];
            const processedData = Array.isArray(data) ? data : [data];
            setConversionData(processedData);
            setFilteredData(processedData);
        } catch (error) {
            console.error("Error fetching point conversion data:", error);
            setConversionData([]);
            setFilteredData([]);
        } finally {
            setLoading(false);
        }
    };

    const getCountryDetailsData = async () => {
        // Placeholder for fetching country details if needed in future
        setLoading(true);
        try {
            const result = await fetchCountryDetailsData(instance, accounts);
            const data = result.apiResponse?.data || [];
            setCountryData(data);
        } catch (error) {
            console.error("Error fetching country details data:", error);
            setCountryData([]);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (value: string) => {
        setSearchText(value);
        if (!value) {
            setFilteredData(conversionData);
            return;
        }

        const filtered = conversionData.filter(item =>
            item.countryName?.toLowerCase().includes(value.toLowerCase()) ||
            item.currencyCode?.toLowerCase().includes(value.toLowerCase()) ||
            item.converionValue?.toString().includes(value) ||
            item.conversionYear?.toString().includes(value)
        );
        setFilteredData(filtered);
    };

    const handleEdit = (record: PointConversionResponse) => {
        setEditingRecord(record);
        form.setFieldsValue({
            countryName: record.countryName,
            currencyCode: record.currencyCode,
            converionValue: record.converionValue,
            conversionYear: record.conversionYear,
        });
        setEditModalVisible(true);
    };

    const handleSaveEdit = async () => {
        try {
            const values = await form.validateFields();
            
            // Update the record in both datasets
            const updatedRecord = { 
                ...editingRecord, 
                ...values,
            };

            const editedRecord = {
                pointConverionId: updatedRecord.pointConverionId,
                converionValue: updatedRecord.converionValue,
                conversionYear: updatedRecord.conversionYear,
                countryName: updatedRecord.countryName,
                currencyCode: updatedRecord.currencyCode,
                updatedBy: userId,
            }

            const result = await updatePointConversionData(instance, accounts, editedRecord as PointConversionResponse);
            if (result.apiResponse && result.apiResponse.status === 200) {
                message.success('Record updated successfully!');
                getPointConversionData(); // Refresh data from server
                 // Close modal and reset form
                setEditModalVisible(false);
                form.resetFields();
            }
        } catch (error) {
            console.error('Updating record failed:', error);
            message.error('Updating record failed.');
        }
    };

    const handleCancelEdit = () => {
        setEditModalVisible(false);
        setEditingRecord(null);
        form.resetFields();
    };

    const handleAddNew = () => {
        setEditingRecord(null);
        form.resetFields();
        setAddModalVisible(true);
    };

    const handleSaveNew = async () => {
        try {
            const values = await form.validateFields();

            const newRecord = {
                countryName: values.countryName,
                currencyCode: values.currencyCode,
                converionValue: values.converionValue,
                conversionYear: values.conversionYear,
                createdBy: userId,
            }

            const result = await savePointConversionData(instance, accounts, newRecord as PointConversionResponse);
            if (result.apiResponse && result.apiResponse.status === 200) {
                message.success('Record added successfully!');
                getPointConversionData(); // Refresh data from server
                 // Close modal and reset form
                setAddModalVisible(false);
                form.resetFields();
            }
        } catch (error) {
            console.error('Saving point conversion failed:', error);
            message.error('Saving new record failed.');
        }
    };

    const handleCancelAdd = () => {
        setAddModalVisible(false);
        form.resetFields();
    };

    const handleCountryChange = (countryName: string) => {
        // Find the selected country from countryData
        const selectedCountry = countryData.find(country => country.countryName === countryName);
        if (selectedCountry) {
            // Set the currency code in the form
            form.setFieldValue('currencyCode', selectedCountry.countryCurrency);
        }
    };

    const columns = [
        {
            title: 'Country',
            dataIndex: 'countryName',
            key: 'countryName',
            sorter: (a: PointConversionResponse, b: PointConversionResponse) => a.countryName.localeCompare(b.countryName),
        },
        {
            title: 'Currency Code',
            dataIndex: 'currencyCode',
            key: 'currencyCode',
            sorter: (a: PointConversionResponse, b: PointConversionResponse) => a.currencyCode.localeCompare(b.currencyCode),
        },
        {
            title: 'Conversion Value',
            dataIndex: 'converionValue',
            key: 'converionValue',
            render: (value: number, record: any) => {
                
                // Handle different data types
                if (value === null || value === undefined) {
                    return 'N/A';
                }
                
                const numValue = Number(value);
                if (isNaN(numValue)) {
                    return 'Invalid';
                }
                
                return numValue.toFixed(4);
            },
            sorter: (a: PointConversionResponse, b: PointConversionResponse) => a.converionValue - b.converionValue,
        },
        {
            title: 'Conversion Year',
            dataIndex: 'conversionYear',
            key: 'conversionYear',
            sorter: (a: PointConversionResponse, b: PointConversionResponse) => a.conversionYear - b.conversionYear,
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 120,
            align: 'center' as const,
            render: (_: any, record: PointConversionResponse) => (
                <Button
                    type="primary"
                    icon={<EditOutlined />}
                    onClick={() => handleEdit(record)}
                    size="small"
                >
                    Edit
                </Button>
            ),
        },
    ];

    return (
        <div>
            <Card bordered={false}>
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        marginBottom: 16,
                        gap: '16px',
                        marginTop: -20,
                    }}>
                        <Title level={2} style={{ margin: 0, marginTop: 0 }}>Point Conversion</Title>
                        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                            <Input
                                placeholder="Search by country, currency code, conversion value, ..."
                                prefix={<SearchOutlined />}
                                value={searchText}
                                onChange={(e) => handleSearch(e.target.value)}
                                style={{ width: 400 }}
                                size="small"
                                allowClear
                            />
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={handleAddNew}
                            >
                                Add New
                            </Button>
                        </div>
                    </div>
                    <Table
                        columns={columns}
                        dataSource={filteredData}
                        rowKey="id"
                        loading={loading}
                        pagination={{
                            pageSize: 50,
                            showSizeChanger: true,
                            showTotal: (total, range) =>
                                `${range[0]}-${range[1]} of ${total} items`,
                        }}
                        scroll={{ x: 800 }}
                    />
                </Space>
            </Card>

            {/* Edit Modal */}
            <Modal
                title="Edit Point Conversion"
                visible={editModalVisible}
                onCancel={handleCancelEdit}
                footer={[
                    <Button key="cancel" onClick={handleCancelEdit} icon={<CloseOutlined />}>
                        Cancel
                    </Button>,
                    <Button 
                        key="save" 
                        type="primary" 
                        onClick={handleSaveEdit}
                        icon={<SaveOutlined />}
                    >
                        Save
                    </Button>,
                ]}
                width={600}
            >
                <Form
                    form={form}
                    layout="vertical"
                    initialValues={{
                        countryName: editingRecord?.countryName,
                        currencyCode: editingRecord?.currencyCode,
                        converionValue: editingRecord?.converionValue,
                        conversionYear: editingRecord?.conversionYear,
                    }}
                >
                    <Form.Item
                        label="Country Name"
                        name="countryName"
                        rules={[{ required: true, message: 'Please select country name' }]}
                    >
                        <Select 
                            placeholder="Select country name"
                            showSearch
                            onChange={handleCountryChange}
                            disabled={true}
                            filterOption={(input, option) =>
                                String(option?.children)?.toLowerCase().includes(input.toLowerCase())
                            }
                        >
                            {countryData.map(country => (
                                <Select.Option key={country.id} value={country.countryName}>
                                    {country.countryName}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        label="Currency Code"
                        name="currencyCode"
                        rules={[{ required: true, message: 'Please select a country to auto-populate currency code' }]}
                    >
                        <Input 
                            placeholder="Currency code will be auto-populated when country is selected" 
                            disabled 
                        />
                    </Form.Item>

                    <Form.Item
                        label="Conversion Value"
                        name="converionValue"
                        rules={[
                            { required: true, message: 'Please enter conversion value' },
                            { type: 'number', min: 0, message: 'Conversion value must be a positive number' },
                            {
                                validator: (_, value) => {
                                    if (value && value.toString().includes('.')) {
                                        const decimalPart = value.toString().split('.')[1];
                                        if (decimalPart && decimalPart.length > 4) {
                                            return Promise.reject(new Error('Maximum 4 decimal places allowed'));
                                        }
                                    }
                                    return Promise.resolve();
                                }
                            }
                        ]}
                    >
                        <InputNumber 
                            placeholder="Enter conversion value (max 4 decimal places)"
                            step={0.0001}
                            precision={4}
                            min={0}
                            style={{ width: '100%' }}
                        />
                    </Form.Item>

                    <Form.Item
                        label="Conversion Year"
                        name="conversionYear"
                        rules={[
                            { required: true, message: 'Please select conversion year' }
                        ]}
                    >
                        <Select 
                            placeholder="Select conversion year"
                            style={{ width: '100%' }}
                            disabled={true}
                        >
                            {Array.from({ length: 25 }, (_, index) => new Date().getFullYear() + index).map(year => (
                                <Select.Option key={year} value={year}>
                                    {year}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>

            {/* Add New Modal */}
            <Modal
                title="Add New Point Conversion"
                visible={addModalVisible}
                onCancel={handleCancelAdd}
                footer={[
                    <Button key="cancel" onClick={handleCancelAdd} icon={<CloseOutlined />}>
                        Cancel
                    </Button>,
                    <Button 
                        key="save" 
                        type="primary" 
                        onClick={handleSaveNew}
                        icon={<SaveOutlined />}
                    >
                        Add
                    </Button>,
                ]}
                width={600}
            >
                <Form
                    form={form}
                    layout="vertical"
                >
                    <Form.Item
                        label="Country Name"
                        name="countryName"
                        rules={[{ required: true, message: 'Please select country name' }]}
                    >
                        <Select 
                            placeholder="Select country name"
                            showSearch
                            onChange={handleCountryChange}
                            filterOption={(input, option) =>
                                String(option?.children)?.toLowerCase().includes(input.toLowerCase())
                            }
                        >
                            {countryData.map(country => (
                                <Select.Option key={country.id} value={country.countryName}>
                                    {country.countryName}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        label="Currency Code"
                        name="currencyCode"
                        rules={[{ required: true, message: 'Please select a country to auto-populate currency code' }]}
                    >
                        <Input 
                            placeholder="Currency code will be auto-populated when country is selected" 
                            disabled 
                        />
                    </Form.Item>

                    <Form.Item
                        label="Conversion Value"
                        name="converionValue"
                        rules={[
                            { required: true, message: 'Please enter conversion value' },
                            { type: 'number', min: 0, message: 'Conversion value must be a positive number' },
                            {
                                validator: (_, value) => {
                                    if (value && value.toString().includes('.')) {
                                        const decimalPart = value.toString().split('.')[1];
                                        if (decimalPart && decimalPart.length > 4) {
                                            return Promise.reject(new Error('Maximum 4 decimal places allowed'));
                                        }
                                    }
                                    return Promise.resolve();
                                }
                            }
                        ]}
                    >
                        <InputNumber 
                            placeholder="Enter conversion value (max 4 decimal places)"
                            step={0.0001}
                            precision={4}
                            min={0}
                            style={{ width: '100%' }}
                        />
                    </Form.Item>

                    <Form.Item
                        label="Conversion Year"
                        name="conversionYear"
                        rules={[
                            { required: true, message: 'Please select conversion year' }
                        ]}
                    >
                        <Select 
                            placeholder="Select conversion year"
                            style={{ width: '100%' }}
                        >
                            {Array.from({ length: 25 }, (_, index) => new Date().getFullYear() + index).map(year => (
                                <Select.Option key={year} value={year}>
                                    {year}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default PointConversion;